import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { toast } from 'sonner';
import { Loader2, Database, CheckCircle2, ShieldCheck, ChevronsRight, RefreshCw, Zap, AlertTriangle, Clock } from 'lucide-react';
import { cn } from '../../lib/utils';
import useCalendarStore from '../../store/useCalendarStore';
import { rbac } from '../../lib/rbac';
import { supabase } from '../../lib/supabase';
import { useEscapeHierarchy } from '../../hooks/useKeyboardShortcuts';
import { useCardNavigation } from '../../hooks/useCardNavigation';
import api from '../../api';

import { BranchesList, BranchDetail } from './BranchPanel';
import { FacultyList, FacultyDetail } from './FacultyPanel';
import { CoursesList, CourseDetail } from './CoursesPanel';
import { BatchesList, BatchDetail } from './BatchesPanel';

// ─── Progress stage definitions ────────────────────────────
const INIT_STAGES = [
    { label: 'Wiping unlocked drafts',    pct: 8  },
    { label: 'Loading batch config',       pct: 16 },
    { label: 'Running CP-SAT solver',      pct: 35 },
    { label: 'Genetic algorithm pass',     pct: 55 },
    { label: 'Simulated annealing',        pct: 72 },
    { label: 'Solidifying rolling window', pct: 84 },
    { label: 'Projecting draft weeks',     pct: 92 },
    { label: 'Persisting to database',     pct: 97 },
];

// ─── Progress overlay component (stable non-blocking corner widget) ────────────────
function InitProgressOverlay({ stage, pct, eventCount, successInfo, isCompleted }) {
    return (
        <div className="fixed bottom-6 right-6 z-[9999] pointer-events-auto animate-in slide-in-from-bottom-5 duration-300">
            <div className="w-[320px] flex flex-col gap-4 p-5 rounded-2xl border border-violet-200 dark:border-violet-900/50 bg-white/95 dark:bg-zinc-900/95 backdrop-blur-md shadow-2xl shadow-violet-500/15">
                {/* Header */}
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2.5">
                        <div className="w-8 h-8 rounded-full bg-violet-100 dark:bg-violet-900/30 flex items-center justify-center shrink-0">
                            {isCompleted ? (
                                <CheckCircle2 className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                            ) : (
                                <Loader2 className="w-4 h-4 animate-spin text-violet-600 dark:text-violet-400" />
                            )}
                        </div>
                        <div className="flex flex-col">
                            <span className="text-[10px] font-black text-zinc-800 dark:text-zinc-100 uppercase tracking-wider leading-none">
                                {isCompleted ? "Sync Completed" : "AI Scheduler Running"}
                            </span>
                            <span className="text-[9px] text-zinc-400 dark:text-zinc-500 uppercase tracking-widest font-bold mt-1 leading-none font-sans">
                                {isCompleted ? "Successfully Synced" : stage}
                            </span>
                        </div>
                    </div>
                    {isCompleted && (
                        <div className="text-[8px] bg-emerald-500/10 text-emerald-500 font-black px-1.5 py-0.5 rounded uppercase tracking-wider">
                            Done
                        </div>
                    )}
                </div>

                {/* Progress bar */}
                <div className="space-y-1">
                    <div className="w-full h-1.5 bg-zinc-100 dark:bg-zinc-800 rounded-full overflow-hidden">
                        <div
                            className={cn(
                                "h-full rounded-full transition-all duration-700 ease-out",
                                isCompleted 
                                    ? "bg-emerald-500" 
                                    : "bg-gradient-to-r from-violet-500 via-indigo-500 to-purple-500"
                            )}
                            style={{ width: `${pct}%` }}
                        />
                    </div>
                    <div className="flex items-center justify-between text-[8px] text-zinc-400 dark:text-zinc-500 font-bold uppercase tracking-wider">
                        <span>{pct}% complete</span>
                        <span>Do not close this tab</span>
                    </div>
                </div>

                {/* Event stats */}
                <div className="pt-3 border-t border-zinc-100 dark:border-zinc-800/80 flex items-center justify-between">
                    <span className="text-[9px] text-zinc-500 dark:text-zinc-400 font-bold uppercase tracking-wider">
                        Installed Events
                    </span>
                    <span className="text-[10px] font-black text-violet-600 dark:text-violet-400 bg-violet-500/5 px-2 py-0.5 rounded-full border border-violet-500/10">
                        {successInfo 
                            ? `${successInfo.total_solid + successInfo.total_draft} (${successInfo.total_solid}S + ${successInfo.total_draft}D)`
                            : `${eventCount} active`
                        }
                    </span>
                </div>
            </div>
        </div>
    );
}

export default function EntitiesPanel() {
    const [data, setData] = useState({
        batches: [], branches: [], faculty: [], courses: [], travelTimes: []
    });
    const [loading, setLoading] = useState(true);

    const isMobile = window.innerWidth < 768;
    const setMobileSidebarOpen = useCalendarStore(s => s.setMobileSidebarOpen);

    const [selectedEntity, setSelectedEntity] = useState(null);
    const [selectedType,   setSelectedType]   = useState(null);

    // ── RBAC flags ──────────────────────────────────────────
    const branchesReadOnly = !rbac.canEditBranches();
    const facultyReadOnly  = !rbac.canEditFaculty();
    const coursesReadOnly  = !rbac.canEditCourses();
    const batchesReadOnly  = !rbac.canEditBatches();

    const fetchData = async () => {
        try {
            const profile = JSON.parse(localStorage.getItem('admin_profile') || '{}');
            const teamId = profile.team_id;

            if (!teamId) {
                setLoading(false);
                return;
            }



            const [baRes, brRes, faRes, crRes, ttRes] = await Promise.all([
                supabase.from('batches').select('*').eq('team_id', teamId),
                supabase.from('branches').select('*').eq('team_id', teamId),
                supabase.from('faculty').select('*').eq('team_id', teamId),
                supabase.from('courses').select('*, subjects:subjects(*)').eq('team_id', teamId),
                supabase.from('travel_times').select('*').eq('team_id', teamId),
            ]);
            if (baRes.error) throw baRes.error;
            if (brRes.error) throw brRes.error;
            if (faRes.error) throw faRes.error;
            if (crRes.error) throw crRes.error;
            if (ttRes.error) throw ttRes.error;
            setData({
                batches:     baRes.data || [],
                branches:    brRes.data || [],
                faculty:     faRes.data || [],
                courses:     crRes.data || [],
                travelTimes: ttRes.data || [],
            });
        } catch (err) {
            toast.error('Failed to load master data');
            console.error(err);
        }
        setLoading(false);
    };

    useEffect(() => { fetchData(); }, []);

    const handleSelect = (entity, type) => {
        setSelectedEntity(entity);
        setSelectedType(type);
    };

    const handleCloseDetail = () => {
        setSelectedEntity(null);
        setSelectedType(null);
    };

    // ─── Build a flat list of all entities for arrow navigation ───
    const flatItems = useMemo(() => {
        const items = [];
        data.branches.forEach(b => items.push({ entity: b, type: 'branch' }));
        data.faculty.forEach(f  => items.push({ entity: f, type: 'faculty' }));
        data.courses.forEach(c  => items.push({ entity: c, type: 'course' }));
        data.batches.forEach(b  => items.push({ entity: b, type: 'batch' }));
        return items;
    }, [data.branches, data.faculty, data.courses, data.batches]);

    // ─── Card Navigation ───
    const { focusedIndex, clearFocus } = useCardNavigation({
        itemCount: flatItems.length,
        enabled: !selectedEntity && !loading,
        onSelect: useCallback((index) => {
            const item = flatItems[index];
            if (item) handleSelect(item.entity, item.type);
        }, [flatItems]),
    });

    // ─── ESC Hierarchy ───
    useEscapeHierarchy([
        { id: 'detail-panel', active: !!selectedEntity, onEscape: handleCloseDetail },
        { id: 'card-focus',   active: focusedIndex !== null, onEscape: clearFocus   },
    ]);

    if (loading) {
        return (
            <div className="flex h-full w-full bg-zinc-50 dark:bg-zinc-950/50" data-testid="entities-panel-loading">
                <div className="flex-1 p-4 grid gap-4 grid-cols-1 md:grid-cols-2 grid-rows-4 md:grid-rows-2 min-h-0">
                    {[1, 2, 3, 4].map((i) => (
                        <div key={i} className="flex flex-col h-full bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-700/50 rounded-xl overflow-hidden shadow-sm animate-pulse min-h-0">
                            <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-200 dark:border-zinc-700/50 shrink-0">
                                <div className="space-y-2">
                                    <div className="h-4 w-32 bg-zinc-200 dark:bg-zinc-800 rounded"></div>
                                    <div className="h-3 w-16 bg-zinc-200 dark:bg-zinc-800 rounded"></div>
                                </div>
                                <div className="h-7 w-16 bg-zinc-200 dark:bg-zinc-800 rounded"></div>
                            </div>
                            <div className="flex-1 p-3 space-y-3 overflow-hidden">
                                {[1, 2, 3].map((j) => (
                                    <div key={j} className="h-14 w-full bg-zinc-100 dark:bg-zinc-800/40 rounded-lg"></div>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    const focusedItem = focusedIndex !== null ? flatItems[focusedIndex] : null;

    return (
        <div className="flex flex-col h-full w-full bg-zinc-50 dark:bg-zinc-950/50 relative" data-testid="entities-panel">




            {/* ── Header ── */}
            <header className="border-b border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 sticky top-0 z-40 shrink-0">
                <div className="flex items-center justify-between px-4 md:px-6 h-14">
                    {/* Left: title */}
                    <div className="flex items-center gap-2">
                        {isMobile && (
                            <button
                                onClick={() => setMobileSidebarOpen(true)}
                                className="w-10 h-10 flex items-center justify-center -ml-1 text-zinc-600 dark:text-zinc-400"
                            >
                                <ChevronsRight className="w-5 h-5" />
                            </button>
                        )}
                        <div className="flex flex-col">
                            <h2 className="font-display text-base md:text-lg font-black tracking-tighter dark:text-white uppercase">
                                Infrastructure Hub
                            </h2>
                            <span className="text-[9px] text-zinc-400 dark:text-zinc-500 font-black uppercase tracking-widest hidden md:inline">
                                Master Data Management
                            </span>
                        </div>
                    </div>
                </div>
            </header>

            {/* ── 2×2 grid ── */}
            <div className="flex-1 p-3 md:p-4 grid gap-3 md:gap-4 grid-cols-1 md:grid-cols-2 grid-rows-4 md:grid-rows-2 overflow-y-auto no-scrollbar min-h-0">
                <div className="min-h-0 h-full flex flex-col">
                    {selectedType === 'branch' && selectedEntity ? (
                        <div className="flex flex-col h-full bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-700/50 rounded-xl overflow-hidden shadow-sm">
                            <BranchDetail
                                branch={selectedEntity.id === 'new' ? selectedEntity : data.branches.find(b => b.id === selectedEntity.id)}
                                branches={data.branches}
                                travelTimes={data.travelTimes}
                                onUpdateBranches={(fn) => setData(d => ({ ...d, branches: typeof fn === 'function' ? fn(d.branches) : fn }))}
                                onUpdateTravelTimes={(fn) => setData(d => ({ ...d, travelTimes: typeof fn === 'function' ? fn(d.travelTimes) : fn }))}
                                onClose={handleCloseDetail}
                                readOnly={branchesReadOnly}
                            />
                        </div>
                    ) : (
                        <BranchesList
                            branches={data.branches}
                            onUpdateBranches={(fn) => setData(d => ({ ...d, branches: typeof fn === 'function' ? fn(d.branches) : fn }))}
                            selectedBranchId={selectedType === 'branch' ? selectedEntity?.id : null}
                            focusedId={focusedItem?.type === 'branch' ? focusedItem.entity.id : null}
                            onSelect={handleSelect}
                            readOnly={branchesReadOnly}
                            onRefresh={fetchData}
                        />
                    )}
                </div>
                <div className="min-h-0 h-full flex flex-col">
                    {selectedType === 'faculty' && selectedEntity ? (
                        <div className="flex flex-col h-full bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-700/50 rounded-xl overflow-hidden shadow-sm">
                            <FacultyDetail
                                facultyItem={selectedEntity.id === 'new' ? selectedEntity : data.faculty.find(f => f.id === selectedEntity.id)}
                                onUpdateFaculty={(fn) => setData(d => ({ ...d, faculty: typeof fn === 'function' ? fn(d.faculty) : fn }))}
                                onClose={handleCloseDetail}
                                readOnly={facultyReadOnly}
                            />
                        </div>
                    ) : (
                        <FacultyList
                            faculty={data.faculty}
                            onUpdateFaculty={(fn) => setData(d => ({ ...d, faculty: typeof fn === 'function' ? fn(d.faculty) : fn }))}
                            selectedFacultyId={selectedType === 'faculty' ? selectedEntity?.id : null}
                            focusedId={focusedItem?.type === 'faculty' ? focusedItem.entity.id : null}
                            onSelect={handleSelect}
                            readOnly={facultyReadOnly}
                            onRefresh={fetchData}
                        />
                    )}
                </div>
                <div className="min-h-0 h-full flex flex-col">
                    {selectedType === 'course' && selectedEntity ? (
                        <div className="flex flex-col h-full bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-700/50 rounded-xl overflow-hidden shadow-sm">
                            <CourseDetail
                                course={selectedEntity.id === 'new' ? selectedEntity : data.courses.find(c => c.id === selectedEntity.id)}
                                branches={data.branches}
                                onUpdateCourses={(fn) => setData(d => ({ ...d, courses: typeof fn === 'function' ? fn(d.courses) : fn }))}
                                onClose={handleCloseDetail}
                                readOnly={coursesReadOnly}
                            />
                        </div>
                    ) : (
                        <CoursesList
                            courses={data.courses}
                            branches={data.branches}
                            onUpdateCourses={(fn) => setData(d => ({ ...d, courses: typeof fn === 'function' ? fn(d.courses) : fn }))}
                            selectedCourseId={selectedType === 'course' ? selectedEntity?.id : null}
                            focusedId={focusedItem?.type === 'course' ? focusedItem.entity.id : null}
                            onSelect={handleSelect}
                            readOnly={coursesReadOnly}
                            onRefresh={fetchData}
                        />
                    )}
                </div>
                <div className="min-h-0 h-full flex flex-col">
                    {selectedType === 'batch' && selectedEntity ? (
                        <div className="flex flex-col h-full bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-700/50 rounded-xl overflow-hidden shadow-sm">
                            <BatchDetail
                                batch={selectedEntity.id === 'new' ? selectedEntity : data.batches.find(b => b.id === selectedEntity.id)}
                                courses={data.courses}
                                branches={data.branches}
                                onUpdateBatches={(fn) => setData(d => ({ ...d, batches: typeof fn === 'function' ? fn(d.batches) : fn }))}
                                onClose={handleCloseDetail}
                                readOnly={batchesReadOnly}
                            />
                        </div>
                    ) : (
                        <BatchesList
                            batches={data.batches}
                            courses={data.courses}
                            branches={data.branches}
                            onUpdateBatches={(fn) => setData(d => ({ ...d, batches: typeof fn === 'function' ? fn(d.batches) : fn }))}
                            selectedBatchId={selectedType === 'batch' ? selectedEntity?.id : null}
                            focusedId={focusedItem?.type === 'batch' ? focusedItem.entity.id : null}
                            onSelect={handleSelect}
                            readOnly={batchesReadOnly}
                            onRefresh={fetchData}
                        />
                    )}
                </div>
            </div>

            {/* Privacy Icon */}
            <div className="absolute bottom-6 right-6 z-50 pointer-events-auto group">
                <div className="w-10 h-10 rounded-full bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 shadow-lg flex items-center justify-center cursor-help transition-all duration-300 group-hover:shadow-violet-500/20 group-hover:border-violet-500/30">
                    <ShieldCheck className="w-5 h-5 text-violet-600 dark:text-violet-400" />
                </div>
                <div className="absolute bottom-full right-0 pb-3 opacity-0 group-hover:opacity-100 invisible group-hover:visible transition-all duration-300 transform translate-y-2 group-hover:translate-y-0 pointer-events-none">
                    <div className="bg-white/95 dark:bg-zinc-900/95 backdrop-blur-md border border-zinc-200 dark:border-zinc-800 p-4 rounded-2xl w-[280px] shadow-2xl flex gap-3 items-start animate-in fade-in slide-in-from-bottom-2">
                        <div className="w-8 h-8 rounded-full bg-violet-100 dark:bg-violet-900/30 flex items-center justify-center shrink-0">
                            <Database className="w-4 h-4 text-violet-600 dark:text-violet-400" />
                        </div>
                        <div>
                            <p className="text-[0.65rem] font-bold text-zinc-800 dark:text-zinc-200 uppercase tracking-widest mb-1.5">Data &amp; Compliance</p>
                            <p className="text-[0.6rem] text-zinc-500 dark:text-zinc-400 leading-relaxed mb-2">
                                This <b>Master Data</b> hub defines your institution&apos;s architecture. AI Studio uses these constraints to generate healthy, conflict-free schedules.
                            </p>
                            <div className="space-y-1.5 border-t border-zinc-100 dark:border-zinc-800 pt-2">
                                <p className="text-[0.55rem] text-emerald-600 dark:text-emerald-400 font-medium flex items-center gap-1">
                                    <CheckCircle2 className="w-2.5 h-2.5" /> SECURE STORAGE
                                </p>
                                <p className="text-[0.55rem] text-zinc-400 leading-tight italic">
                                    Academic data is used exclusively for internal optimization. No third-party sharing or advertising use.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
